import React, { useMemo, useState, useRef, useCallback } from 'react';
import { Popover, Input } from 'antd';
import { useNavigate } from 'react-router-dom';
import { DownOutlined, SearchOutlined } from '@ant-design/icons';
import { metricsStore } from '@src/globalStore/metricsStore';
import { useFrustrationSignalConfigs } from '@src/hooks/frustrationSignal/useFrustrationSignalConfigs';
import presetStore from '@src/globalStore/presetsStore';
import useTopEvents from '../hooks/useTopEvents';
import {
  FlowLabel,
  FlowMainPanel,
  DropdownTrigger,
  DropdownIcon,
  StyledFlowTag,
  FlowModalContent,
  FlowModalSidebar,
  FlowModalMain,
  FlowItem,
  SearchWrapper,
  EmptyState,
  LabelsSection,
  LabelsTitle,
  LabelsGroup,
  FlowItemName,
  FlowItemTags,
  StyledCheckableTag,
  StageMenuItem,
  FlowMetaTag,
} from './styled';

interface CombinedItem {
  id: string;
  name: string;
  type: 'flow' | 'frustration-signal' | 'event' | 'preset';
  labels?: string[];
}

interface CombinedBreadcrumbDropdownProps {
  // Flow props
  flowId?: string;
  // Frustration signal props
  signalId?: string;
  // Event props
  eventId?: string;
  // Common props
  title?: string;
  type: 'flow' | 'frustration-signal' | 'event' | 'preset';
  // Preset props
  presetId?: string;
}

// Helper function to get color and text based on type
const getTypeConfig = (type: 'flow' | 'frustration-signal' | 'event' | 'preset') => {
  switch (type) {
    case 'flow':
      return { color: '#52c41a', text: 'Flow' };
    case 'frustration-signal':
      return { color: '#ff4d4f', text: 'Frustration Signal' };
    case 'event':
      return { color: '#91d5ff', text: 'Event' };
    case 'preset':
      return { color: '#1677ff', text: 'Preset' };
    default:
      return { color: '#1677ff', text: 'Preset' };
  }
};

const CombinedBreadcrumbDropdown: React.FC<CombinedBreadcrumbDropdownProps> = ({
  flowId,
  signalId,
  eventId,
  presetId,
  title,
  type,
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<
    'all' | 'flows' | 'frustration-signals' | 'events' | 'presets'
  >(
    type === 'flow'
      ? 'flows'
      : type === 'frustration-signal'
        ? 'frustration-signals'
        : type === 'event'
          ? 'events'
          : 'presets',
  );
  const searchInputRef = useRef<any>(null);

  // Get data from the stores - memoize to prevent dependency issues
  const flowConfigMetaRaw = metricsStore((state) => state.flowConfigMeta);
  const flowConfigMeta = useMemo(() => flowConfigMetaRaw || {}, [flowConfigMetaRaw]);

  const frustrationSignalConfigMeta = useFrustrationSignalConfigs();

  // Fetch top 200 events from group-by API
  const { eventList: topEventsList } = useTopEvents();

  const standardPresetsRaw = presetStore((state) => state.presets);
  const standardPresets = useMemo(() => standardPresetsRaw || {}, [standardPresetsRaw]);

  // Focus search input when popover opens
  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      setIsPopoverOpen(open);
      if (open && searchInputRef.current) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 0);
      }
    },
    [setIsPopoverOpen],
  );

  // Get all unique labels from both flows and signals
  const allLabels = useMemo(() => {
    const labelSet = new Set<string>();

    // Add flow labels
    Object.values(flowConfigMeta).forEach((flow: any) => {
      if (flow.labels && Array.isArray(flow.labels)) {
        flow.labels.forEach((label: string) => {
          if (label) labelSet.add(label);
        });
      }
    });

    // Frustration signals typically don't have labels, but we can support them if needed
    Object.values(frustrationSignalConfigMeta).forEach((signal: any) => {
      if (signal.labels && Array.isArray(signal.labels)) {
        signal.labels.forEach((label: string) => {
          if (label) labelSet.add(label);
        });
      }
    });

    return Array.from(labelSet).sort();
  }, [flowConfigMeta, frustrationSignalConfigMeta]);

  // Convert flows, signals, events, and presets to combined format
  const allItems = useMemo(() => {
    const items: CombinedItem[] = [];

    // Add flows
    Object.entries(flowConfigMeta).forEach(([id, meta]) => {
      items.push({
        id,
        name: (meta as any).name,
        type: 'flow',
        labels: (meta as any).labels || [],
      });
    });

    // Add frustration signals
    Object.entries(frustrationSignalConfigMeta).forEach(([id, meta]) => {
      items.push({
        id,
        name: (meta as any).name,
        type: 'frustration-signal',
        labels: (meta as any).labels || [],
      });
    });

    // Add events from top 200 list
    topEventsList.forEach((eventName) => {
      items.push({
        id: eventName,
        name: eventName,
        type: 'event',
        labels: [],
      });
    });

    // Add standard presets (Web Experience, Mobile Experience, etc.)
    Object.entries(standardPresets).forEach(([id, preset]) => {
      // Skip hidden presets
      if ((preset as any).hide) return;

      items.push({
        id,
        name: (preset as any).title,
        type: 'preset',
        labels: [],
      });
    });

    return items;
  }, [flowConfigMeta, frustrationSignalConfigMeta, topEventsList, standardPresets]);

  // Filter items based on active tab, search term, and labels
  const filteredItems = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();

    const filtered = allItems.filter((item) => {
      // Tab filter
      if (activeTab === 'flows' && item.type !== 'flow') return false;
      if (activeTab === 'frustration-signals' && item.type !== 'frustration-signal') return false;
      if (activeTab === 'events' && item.type !== 'event') return false;
      if (activeTab === 'presets' && item.type !== 'preset') return false;

      // Search filter
      if (searchTerm && !item.name?.toLowerCase().includes(lowerSearch)) {
        return false;
      }

      // Label filter (only apply to flows since signals and presets typically don't have labels)
      if (selectedLabels.length > 0 && item.type === 'flow') {
        if (!item.labels?.some((label) => selectedLabels.includes(label))) {
          return false;
        }
      }

      return true;
    });

    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  }, [allItems, activeTab, searchTerm, selectedLabels]);

  const handleItemClick = (item: CombinedItem) => {
    if (item.type === 'event') {
      navigate(`/app/app-insights/dashboard/preset/event/${item.id}`);
    } else if (item.type === 'flow') {
      navigate(`/app/app-insights/dashboard/preset/flow/${item.id}`);
    } else if (item.type === 'frustration-signal') {
      navigate(`/app/app-insights/dashboard/preset/frustration-signal/${item.id}`);
    } else if (item.type === 'preset') {
      navigate(`/app/app-insights/dashboard/preset/${item.id}`);
    }
    setIsPopoverOpen(false);
  };

  const toggleLabel = (label: string) => {
    setSelectedLabels((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label],
    );
  };

  const handleTabChange = (tab: 'all' | 'flows' | 'frustration-signals' | 'events' | 'presets') => {
    setActiveTab(tab);
    // Clear label filters when switching tabs since signals and presets don't have labels
    if (tab === 'frustration-signals' || tab === 'events' || tab === 'presets' || tab === 'all') {
      setSelectedLabels([]);
    }
  };

  // Get current item for display
  const currentItem = flowId
    ? flowConfigMeta[flowId]
    : signalId
      ? frustrationSignalConfigMeta[signalId]
      : eventId
        ? { name: eventId }
        : presetId
          ? standardPresets[presetId]
          : null;
  const currentId = flowId || signalId || eventId || presetId;

  const popoverContent = (
    <FlowModalContent>
      <FlowModalSidebar>
        <StageMenuItem $isActive={activeTab === 'all'} onClick={() => handleTabChange('all')}>
          All
        </StageMenuItem>
        <StageMenuItem $isActive={activeTab === 'flows'} onClick={() => handleTabChange('flows')}>
          Flows
        </StageMenuItem>
        <StageMenuItem
          $isActive={activeTab === 'frustration-signals'}
          onClick={() => handleTabChange('frustration-signals')}
        >
          Frustrations
        </StageMenuItem>
        <StageMenuItem $isActive={activeTab === 'events'} onClick={() => handleTabChange('events')}>
          Events
        </StageMenuItem>
        <StageMenuItem
          $isActive={activeTab === 'presets'}
          onClick={() => handleTabChange('presets')}
        >
          Presets
        </StageMenuItem>
      </FlowModalSidebar>

      <FlowModalMain>
        <SearchWrapper>
          <Input
            ref={searchInputRef}
            placeholder={`Search ${activeTab === 'all' ? 'flows & frustrations' : activeTab}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            prefix={<SearchOutlined />}
            allowClear
            size="large"
          />
        </SearchWrapper>

        {allLabels.length > 0 && (activeTab === 'flows' || activeTab === 'all') && (
          <LabelsSection>
            <LabelsTitle>Flow Labels:</LabelsTitle>
            <LabelsGroup>
              {allLabels.map((label) => (
                <StyledCheckableTag
                  key={label}
                  checked={selectedLabels.includes(label)}
                  $checked={selectedLabels.includes(label)}
                  onChange={() => toggleLabel(label)}
                >
                  {label}
                </StyledCheckableTag>
              ))}
            </LabelsGroup>
          </LabelsSection>
        )}

        {filteredItems.length === 0 ? (
          <EmptyState>
            {searchTerm
              ? `No items found matching "${searchTerm}"`
              : `No ${activeTab === 'all' ? 'items' : activeTab} available`}
          </EmptyState>
        ) : (
          <FlowMainPanel>
            {filteredItems.map((item) => (
              <FlowItem
                key={`${item.type}-${item.id}`}
                onClick={() => handleItemClick(item)}
                $isSelected={item.id === currentId && item.type === type}
                $type={item.type}
              >
                <FlowItemName>{item.name}</FlowItemName>
                {item.labels && item.labels.length > 0 && (
                  <FlowItemTags>
                    {item.labels.map((label: string) => (
                      <FlowMetaTag key={label}>{label}</FlowMetaTag>
                    ))}
                  </FlowItemTags>
                )}
              </FlowItem>
            ))}
          </FlowMainPanel>
        )}
      </FlowModalMain>
    </FlowModalContent>
  );

  // Determine tag color and text based on type
  const { color: tagColor, text: tagText } = getTypeConfig(type);

  // Presets use 'title' property, flows and signals use 'name'
  const displayName = currentItem?.name || currentItem?.title || title || 'Deleted Item';

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={isPopoverOpen}
      onOpenChange={handlePopoverOpenChange}
      placement="bottomLeft"
    >
      <DropdownTrigger>
        <StyledFlowTag color={tagColor}>{tagText}</StyledFlowTag>
        <FlowLabel title={displayName}>{displayName}</FlowLabel>
        <DropdownIcon>
          <DownOutlined />
        </DropdownIcon>
      </DropdownTrigger>
    </Popover>
  );
};

export default CombinedBreadcrumbDropdown;
